import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Refresh as ResendIcon,
  AccessTime as PendingIcon
} from '@mui/icons-material';
import axios from '../utils/axiosConfig';

const GroupInvitationManager = ({ groupId, onUpdate }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    fetchInvitations();
  }, [groupId]);

  const fetchInvitations = async () => {
    try {
      console.log('Fetching invitations for group:', groupId);
      const response = await axios.get(`/api/group-invitations/group/${groupId}`);
      console.log('Invitations response:', response.data);
      setInvitations(response.data.invitations);
      setError(null);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setError('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
        console.log('Canceling invitation:', invitationId);
        const response = await axios.delete(`/api/group-invitations/${invitationId}`);
        
        if (response.data.success) {
            // Remove the canceled invitation from state
            setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
            setError(null);
        }
    } catch (error) {
        console.error('Error canceling invitation:', error);
        setError('Failed to cancel invitation');
    }
  };

  const handleResendInvitation = async (invitation) => {
    try {
      await axios.post(`/api/group-invitations/${invitation.id}/resend`);
      await fetchInvitations();
      onUpdate?.();
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError('Failed to resend invitation');
    }
  };

  const getStatusChip = (status, expiresAt) => {
    const isExpired = new Date(expiresAt) < new Date();
    
    if (isExpired) {
      return <Chip size="small" label="Expired" color="error" />;
    }
    
    switch (status) {
      case 'pending':
        return <Chip size="small" label="Pending" color="warning" icon={<PendingIcon />} />;
      case 'accepted':
        return <Chip size="small" label="Accepted" color="success" />;
      default:
        return null;
    }
  };

  return (
    <Box>
        <Typography variant="h6" gutterBottom>
            Pending Invitations
        </Typography>
        
        {loading ? (
            <Typography>Loading invitations...</Typography>
        ) : error ? (
            <Typography color="error">{error}</Typography>
        ) : invitations.length === 0 ? (
            <Typography color="textSecondary">
                No pending invitations
            </Typography>
        ) : (
            <List>
                {invitations.map((invitation) => (
                    <ListItem key={invitation.id}>
                        <ListItemText 
                            primary={invitation.email}
                            secondary={`Invited by: ${invitation.inviter?.first_name} ${invitation.inviter?.last_name}`}
                        />
                        <ListItemSecondaryAction>
                            {getStatusChip(invitation.status, invitation.expires_at)}
                            <Tooltip title="Cancel">
                                <IconButton onClick={() => handleCancelInvitation(invitation.id)}>
                                    <CancelIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Resend">
                                <IconButton onClick={() => handleResendInvitation(invitation)}>
                                    <ResendIcon />
                                </IconButton>
                            </Tooltip>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
        )}
    </Box>
  );
};

export default GroupInvitationManager; 